import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { getAssignedWritings } from 'services/escriturasMineService';
import { escriturasMineI18n } from './i18n';
import { formatDate } from './format';

// Estados que aún están "en curso" → Badge informativo; los de notaría/cancelada
// son finales y se pintan con su color semántico.
const statusColor = (estado) => {
  switch ((estado || '').toLowerCase()) {
    case 'firmada':
      return 'success';
    case 'rechazada_notaria':
    case 'cancelada':
      return 'error';
    case 'observada_notaria':
      return 'warning';
    case 'completada':
      return 'info';
    default:
      return 'light';
  }
};

// Color del badge de estado SLA (Contract): vencido=error, en plazo=neutral,
// cerrado=success. null (fila pre-deploy sin reloj) → sin badge ("—").
const slaColor = (slaStatus) => {
  switch (slaStatus) {
    case 'due':
      return 'error';
    case 'closed':
      return 'success';
    case 'pending':
      return 'light';
    default:
      return 'light';
  }
};

// Etapas del flujo (flowStage) en el orden de precedencia del Contract; alimenta
// el Dropdown de filtro por etapa (client-side).
const STAGE_VALUES = [
  'recoleccion',
  'redaccion',
  'validacion',
  'notaria',
  'finalizada',
  'cancelada',
];

// Estados SLA para el Dropdown de filtro por SLA (client-side).
const SLA_STATUS_VALUES = ['pending', 'due', 'closed'];

/**
 * Vista global "Mis escrituras" (submenú EEPP) — espejo de la vista "Log de Correos".
 *
 * Lista TODAS las escrituras asignadas al validador (abogado EEPP) logueado, a través
 * de todos los casos, en una sola pantalla. El server resuelve "yo" desde el token; la
 * vista solo pide GET /escrituras/assigned. Cada fila enlaza al detalle del cliente
 * en la pestaña EEPP BETA, con scroll a la escritura
 * (/crm/detalles-cliente/{clientId}?tab=eeppBeta&escrituraId={escrituraId}).
 *
 * Cada fila muestra el SLA de la escritura (fecha de vencimiento, días hábiles
 * restantes y badge de estado) y la etapa del flujo (chip). Dos filtros
 * client-side (Dropdown por etapa + Dropdown por estado SLA) acotan la lista sin
 * volver a pedir al server.
 */
export default function EscriturasMine({ DesignSystemComponents }) {
  const { t } = useScopedTranslation(escriturasMineI18n);

  const { Table, Badge, Card, Skeleton, Button, Dropdown } =
    DesignSystemComponents ?? {};

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros client-side (no re-piden al server): 'all' = sin filtro.
  const [stageFilter, setStageFilter] = useState('all');
  const [slaFilter, setSlaFilter] = useState('all');

  const abortRef = useRef(null);

  const fetchList = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const data = await getAssignedWritings(controller.signal);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('mine_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchList();
    return () => abortRef.current?.abort();
  }, [fetchList]);

  const statusBadge = useCallback(
    (estado) => {
      const key = (estado || '').toLowerCase();
      const label = t(`mine_status_${key}`) || estado || '—';
      return Badge ? (
        <Badge text={label} color={statusColor(estado)} size='sm' />
      ) : (
        <span>{label}</span>
      );
    },
    [Badge, t]
  );

  // Badge de estado SLA. null (fila pre-deploy sin reloj) → "—" sin badge.
  const slaBadge = useCallback(
    (slaStatus) => {
      if (!slaStatus) return <span>—</span>;
      const label = t(`em_sla_badge_${slaStatus}`) || slaStatus;
      return Badge ? (
        <Badge text={label} color={slaColor(slaStatus)} size='sm' />
      ) : (
        <span>{label}</span>
      );
    },
    [Badge, t]
  );

  // Chip de etapa del flujo. flowStage siempre viene (salvo filas sin SLA → "—").
  const stageBadge = useCallback(
    (flowStage) => {
      if (!flowStage) return <span>—</span>;
      const label = t(`em_stage_${flowStage}`) || flowStage;
      return Badge ? (
        <Badge text={label} color='light' size='sm' />
      ) : (
        <span>{label}</span>
      );
    },
    [Badge, t]
  );

  const stageOptions = useMemo(
    () => [
      { label: t('em_filter_all'), value: 'all' },
      ...STAGE_VALUES.map((value) => ({
        label: t(`em_stage_${value}`),
        value,
      })),
    ],
    [t]
  );
  const selectedStageOption =
    stageOptions.find((o) => o.value === stageFilter) || stageOptions[0];

  const slaOptions = useMemo(
    () => [
      { label: t('em_filter_all'), value: 'all' },
      ...SLA_STATUS_VALUES.map((value) => ({
        label: t(`em_sla_badge_${value}`),
        value,
      })),
    ],
    [t]
  );
  const selectedSlaOption =
    slaOptions.find((o) => o.value === slaFilter) || slaOptions[0];

  // Filtrado client-side por etapa y estado SLA (ambos 'all' = sin filtro).
  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (stageFilter !== 'all' && row.flowStage !== stageFilter)
          return false;
        if (slaFilter !== 'all' && row.slaStatus !== slaFilter) return false;
        return true;
      }),
    [rows, stageFilter, slaFilter]
  );

  const columns = useMemo(
    () => [
      {
        accessor: 'caseId',
        label: t('mine_col_case'),
        // El nombre del cliente viene del brief del intake; si no hay, mostramos el
        // id del caso como label. El enlace abre el detalle del cliente en la pestaña
        // EEPP BETA y hace scroll a la escritura (?tab=eeppBeta&escrituraId=...). Si el
        // caso no resolvió cliente (clientId null → caso huérfano), no enlazamos para
        // no generar /crm/detalles-cliente/undefined.
        render: (row) => {
          const label = row.clientName || `#${row.caseId}`;
          // El enlace pre-selecciona el caso (?caseId=) para que la pestaña abra el
          // caso correcto cuando el cliente tiene varios; si por algún motivo no hay
          // caseId, lo omitimos (no emitimos caseId=undefined).
          const caseParam = row.caseId ? `&caseId=${row.caseId}` : '';
          return row.clientId ? (
            <Link
              to={`/crm/detalles-cliente/${row.clientId}?tab=eeppBeta${caseParam}&escrituraId=${row.escrituraId}`}
              className='text-primary text-decoration-none'
            >
              {label}
            </Link>
          ) : (
            label
          );
        },
      },
      {
        accessor: 'writingTypeDisplayName',
        label: t('mine_col_type'),
        render: (row) =>
          row.writingTypeDisplayName || row.writingTypeCode || '—',
      },
      {
        accessor: 'estado',
        label: t('mine_col_status'),
        // align-items-start: el Badge (size='sm') queda inline y no se estira al
        // ancho de la celda.
        render: (row) => (
          <div className='d-flex align-items-start'>
            {statusBadge(row.estado)}
          </div>
        ),
      },
      {
        accessor: 'flowStage',
        label: t('em_col_stage'),
        render: (row) => (
          <div className='d-flex align-items-start'>
            {stageBadge(row.flowStage)}
          </div>
        ),
      },
      {
        accessor: 'slaDueAt',
        label: t('em_col_sla_due'),
        render: (row) => formatDate(row.slaDueAt),
      },
      {
        accessor: 'slaDaysLeft',
        label: t('em_col_sla_days'),
        // Días hábiles restantes (negativo = vencido). El badge de estado da el
        // color; aquí el número crudo. null (sin reloj) → "—".
        render: (row) => (
          <div className='d-flex align-items-center' style={{ gap: 6 }}>
            <span>{row.slaDaysLeft == null ? '—' : row.slaDaysLeft}</span>
            {slaBadge(row.slaStatus)}
          </div>
        ),
      },
      {
        accessor: 'createdAt',
        label: t('mine_col_date'),
        render: (row) => formatDate(row.createdAt),
      },
    ],
    [t, statusBadge, stageBadge, slaBadge]
  );

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('mine_title')}</h2>
          <h4 className='fw-normal'>{t('mine_subtitle')}</h4>
        </div>
        {Button && (
          <Button
            label={t('mine_refresh')}
            size='sm'
            variant='naked'
            onClick={fetchList}
            disabled={loading}
          />
        )}
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Filtros client-side: etapa del flujo + estado SLA. */}
        <div className='d-flex flex-wrap gap-2 mb-3'>
          <div style={{ flex: '0 1 220px' }}>
            {Dropdown ? (
              <Dropdown
                options={stageOptions}
                value={selectedStageOption}
                onChange={(option) => setStageFilter(option?.value || 'all')}
                placeholder={t('em_filter_stage_placeholder')}
                width='100%'
                isDisabled={loading}
              />
            ) : (
              <select
                className='form-select form-select-sm'
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
              >
                {stageOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div style={{ flex: '0 1 220px' }}>
            {Dropdown ? (
              <Dropdown
                options={slaOptions}
                value={selectedSlaOption}
                onChange={(option) => setSlaFilter(option?.value || 'all')}
                placeholder={t('em_filter_sla_placeholder')}
                width='100%'
                isDisabled={loading}
              />
            ) : (
              <select
                className='form-select form-select-sm'
                value={slaFilter}
                onChange={(e) => setSlaFilter(e.target.value)}
              >
                {slaOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {loading && rows.length === 0 ? (
          <div className='d-flex flex-column gap-2'>
            {Skeleton ? (
              [0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height='2.5rem' width='100%' />
              ))
            ) : (
              <p className='font-light'>{t('mine_loading')}</p>
            )}
          </div>
        ) : (
          <div className='table-container'>
            {Table ? (
              <Table columns={columns} data={filteredRows} loading={loading} />
            ) : (
              <p className='font-light'>{loading ? t('mine_loading') : null}</p>
            )}
            {!loading && filteredRows.length === 0 && (
              <div className='text-center p-4'>
                <p className='caption font-light'>{t('mine_empty')}</p>
              </div>
            )}
          </div>
        )}
      </ContentWrapper>
    </div>
  );
}
